<template>
  <div class="creq">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('requests.myTitle') }}</h1>
        <p class="page-sub">{{ t('requests.mySub') }}</p>
      </div>
      <button class="btn btn-primary" @click="showForm = !showForm">+ {{ t('requests.newBtn') }}</button>
    </div>

    <div v-if="showForm" class="req-form">
      <div class="seg">
        <button type="button" :class="{ active: form.type === 'project' }" @click="form.type = 'project'">{{ t('requests.typeProject') }}</button>
        <button type="button" :class="{ active: form.type === 'task' }" @click="form.type = 'task'">{{ t('requests.typeTask') }}</button>
      </div>
      <div class="form-group">
        <label>{{ t('requests.fieldTitle') }} *</label>
        <input v-model="form.title" class="form-control" maxlength="200" :placeholder="t('requests.titlePh')" />
      </div>
      <div class="form-group">
        <label>{{ t('requests.fieldDesc') }}</label>
        <textarea v-model="form.description" class="form-control" rows="3" :placeholder="t('requests.descPh')"></textarea>
      </div>
      <div v-if="form.type === 'task'" class="form-group">
        <label>{{ t('requests.fieldProject') }} *</label>
        <select v-model="form.project_id" class="form-control">
          <option :value="null">{{ t('requests.selectProject') }}</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
      </div>
      <div v-if="form.type === 'project'" class="form-group">
        <label>{{ t('requests.billingPref') }}</label>
        <select v-model="form.desired_pricing_type" class="form-control">
          <option value="none">{{ t('projects.pricingNone') }}</option>
          <option value="fixed">{{ t('projects.pricingFixed') }}</option>
          <option value="hourly">{{ t('projects.pricingHourly') }}</option>
        </select>
        <input v-if="form.desired_pricing_type !== 'none'" v-model="form.desired_price_amount" type="number" min="0" step="0.01" class="form-control amt" :placeholder="t('requests.amount')" />
      </div>
      <p v-if="error" class="form-error">{{ error }}</p>
      <div class="modal-actions">
        <button type="button" class="btn btn-ghost" @click="showForm = false">{{ t('common.cancel') }}</button>
        <button type="button" class="btn btn-primary" :disabled="!canSubmit || saving" @click="submit">
          {{ saving ? t('common.saving') : t('requests.submit') }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>
    <div v-else-if="requests.length === 0" class="empty-state"><p>{{ t('requests.empty') }}</p></div>
    <div v-else class="req-list">
      <div v-for="r in requests" :key="r.id" class="req-item">
        <div class="req-main">
          <div class="req-title">{{ r.title }}</div>
          <div class="req-meta">{{ r.type === 'project' ? t('requests.typeProject') : t('requests.typeTask') }} · {{ fmtDate(r.created_at) }}</div>
        </div>
        <span :class="['req-status', 'st-' + r.status]">{{ t('requests.' + r.status) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { onRealtime, debounce } from '@/composables/useRealtime'

export default {
  name: 'ClientRequests',
  setup() {
    const { t, locale } = useI18n()
    const requests = ref([])
    const projects = ref([])
    const loading = ref(true)
    const showForm = ref(false)
    const saving = ref(false)
    const error = ref('')
    const form = reactive({ type: 'project', title: '', description: '', project_id: null, desired_pricing_type: 'none', desired_price_amount: '' })

    const canSubmit = computed(() => form.title.trim() && (form.type !== 'task' || form.project_id))

    function fmtDate(dt) {
      if (!dt) return ''
      return new Date(dt.replace(' ', 'T')).toLocaleDateString(locale.value === 'he' ? 'he-IL' : 'en-GB', { day: '2-digit', month: 'short' })
    }

    async function load() {
      loading.value = true
      try {
        const [rr, pr] = await Promise.all([api.getRequests(), api.getProjects()])
        requests.value = rr.data || []
        projects.value = pr.data || []
      } catch {} finally { loading.value = false }
    }

    async function submit() {
      error.value = ''
      saving.value = true
      try {
        await api.createRequest({
          type: form.type,
          title: form.title,
          description: form.description || null,
          project_id: form.type === 'task' ? form.project_id : null,
          desired_pricing_type: form.type === 'project' ? form.desired_pricing_type : 'none',
          desired_price_amount: form.type === 'project' && form.desired_pricing_type !== 'none' && form.desired_price_amount !== '' ? Number(form.desired_price_amount) : null,
        })
        Object.assign(form, { title: '', description: '', project_id: null, desired_pricing_type: 'none', desired_price_amount: '' })
        showForm.value = false
        await load()
      } catch (e) {
        const errs = e.data && e.data.errors
        error.value = errs ? Object.values(errs).flat().join(' ') : (e.message || 'Failed')
      } finally { saving.value = false }
    }

    let off = () => {}
    onMounted(() => { load(); off = onRealtime('request.changed', debounce(load, 400)) })
    onUnmounted(() => off())

    return { t, requests, projects, loading, showForm, saving, error, form, canSubmit, submit, fmtDate }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.req-form { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 20px; margin-bottom: 22px; max-width: 540px; }
.seg { display: inline-flex; border: 1px solid var(--border); border-radius: 10px; overflow: hidden; margin-bottom: 16px; }
.seg button { border: none; background: var(--surface); color: var(--muted); padding: 8px 18px; font: inherit; font-size: 13px; cursor: pointer; }
.seg button.active { background: var(--accent); color: var(--on-accent); font-weight: 600; }
.amt { margin-top: 8px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }

.req-list { display: flex; flex-direction: column; gap: 8px; }
.req-item { display: flex; align-items: center; gap: 12px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 14px 16px; }
.req-main { flex: 1; min-width: 0; }
.req-title { font-size: 14px; font-weight: 500; }
.req-meta { font-size: 12px; color: var(--muted); margin-top: 2px; }
.req-status { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.st-pending  { background: var(--accent-wash); color: var(--accent); }
.st-approved { background: rgba(30,138,83,.12); color: var(--success); }
.st-declined { background: rgba(209,68,68,.12); color: var(--danger); }
</style>
